<template>
  <div class="data-table">
    <div class="data-table__toolbar">
      <div class="data-table__search">
        <feather-icon icon="SearchIcon" svgClasses="w-4 h-4" />
        <input
          v-model="quickFilter"
          type="text"
          :placeholder="searchPlaceholder"
          class="data-table__search-input"
        />
      </div>

      <div class="data-table__actions">
        <label class="data-table__page-size">
          <span>Mostrar</span>
          <select v-model.number="localPerPage">
            <option
              v-for="option in perPageOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>
        </label>

        <button type="button" class="data-table__clear" @click="clearFilters">
          Limpiar filtros
        </button>
      </div>
    </div>

    <div
      ref="grid"
      class="ag-theme-material data-table__grid"
      :style="{ height: height }"
    ></div>
  </div>
</template>

<script>
import 'ag-grid-community/dist/styles/ag-grid.css'
import 'ag-grid-community/dist/styles/ag-theme-material.css'
import { Grid } from 'ag-grid-community'

const FILTER_OPTIONS = [
  { value: 'contains', label: 'Contiene' },
  { value: 'notContains', label: 'No contiene' },
  { value: 'equals', label: 'Igual' },
  { value: 'notEqual', label: 'No igual' },
  { value: 'startsWith', label: 'Empieza con' },
  { value: 'endsWith', label: 'Termina con' },
]

class DataTableTextFilter {
  init(params) {
    this.params = params
    this.type = 'contains'
    this.filterText = ''
    this.gui = document.createElement('div')
    this.gui.className = 'data-table-filter'

    this.gui.innerHTML = `
      <div class="data-table-filter__options">
        ${FILTER_OPTIONS.map(option => `
          <button
            type="button"
            class="data-table-filter__option${option.value === this.type ? ' is-active' : ''}"
            data-filter-type="${option.value}"
          >
            ${option.label}
          </button>
        `).join('')}
      </div>
      <div class="data-table-filter__field">
        <input
          type="text"
          class="data-table-filter__input"
          placeholder="Filtrar..."
        />
      </div>
    `

    this.input = this.gui.querySelector('.data-table-filter__input')
    this.buttons = Array.prototype.slice.call(this.gui.querySelectorAll('.data-table-filter__option'))

    this.onInput = event => {
      this.filterText = event.target.value || ''
      this.params.filterChangedCallback()
    }

    this.onOptionClick = event => {
      const selectedType = event.currentTarget.getAttribute('data-filter-type')
      this.type = selectedType
      this.syncActiveOption()
      this.params.filterChangedCallback()
    }

    this.input.addEventListener('input', this.onInput)
    this.buttons.forEach(button => {
      button.addEventListener('click', this.onOptionClick)
    })
  }

  getGui() {
    return this.gui
  }

  afterGuiAttached() {
    window.setTimeout(() => {
      if (this.input) {
        this.input.focus()
      }
    }, 0)
  }

  isFilterActive() {
    return this.filterText.trim() !== ''
  }

  doesFilterPass(params) {
    const value = this.normalize(this.params.valueGetter(params.node))
    const filterText = this.normalize(this.filterText)

    switch (this.type) {
      case 'notContains':
        return value.indexOf(filterText) === -1
      case 'equals':
        return value === filterText
      case 'notEqual':
        return value !== filterText
      case 'startsWith':
        return value.indexOf(filterText) === 0
      case 'endsWith':
        return value.lastIndexOf(filterText) === value.length - filterText.length
      case 'contains':
      default:
        return value.indexOf(filterText) !== -1
    }
  }

  getModel() {
    if (!this.isFilterActive()) {
      return null
    }

    return {
      type: this.type,
      filter: this.filterText,
    }
  }

  setModel(model) {
    this.type = model && model.type ? model.type : 'contains'
    this.filterText = model && model.filter ? model.filter : ''

    if (this.input) {
      this.input.value = this.filterText
    }

    this.syncActiveOption()
  }

  destroy() {
    if (this.input) {
      this.input.removeEventListener('input', this.onInput)
    }

    if (this.buttons) {
      this.buttons.forEach(button => {
        button.removeEventListener('click', this.onOptionClick)
      })
    }
  }

  syncActiveOption() {
    if (!this.buttons) {
      return
    }

    this.buttons.forEach(button => {
      const isActive = button.getAttribute('data-filter-type') === this.type
      button.classList.toggle('is-active', isActive)
    })
  }

  normalize(value) {
    return String(value === null || value === undefined ? '' : value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  }
}

export default {
  name: 'DataTable',
  props: {
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    rowKey: {
      type: String,
      default: 'id',
    },
    searchPlaceholder: {
      type: String,
      default: 'Buscar...',
    },
    perPage: {
      type: Number,
      default: 10,
    },
    perPageOptions: {
      type: Array,
      default: () => [10, 15, 25, 50],
    },
    height: {
      type: String,
      default: '420px',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      grid: null,
      gridApi: null,
      columnApi: null,
      quickFilter: '',
      localPerPage: this.perPage,
      autoRowIds: new WeakMap(),
      autoRowIdSeq: 0,
      gridOptions: {
        columnDefs: [],
        rowData: [],
        pagination: true,
        paginationPageSize: this.perPage,
        suppressCellSelection: true,
        enableColResize: true,
        enableSorting: true,
        enableFilter: true,
        floatingFilter: false,
        defaultColDef: {
          sortable: true,
          filter: DataTableTextFilter,
          resizable: true,
          floatingFilter: false,
        },
        localeText: {
          contains: 'Contiene',
          notContains: 'No contiene',
          equals: 'Igual',
          notEqual: 'No igual',
          startsWith: 'Empieza con',
          endsWith: 'Termina con',
          filterOoo: 'Filtrar...',
          applyFilter: 'Aplicar',
          clearFilter: 'Limpiar',
          noRowsToShow: 'No hay datos para mostrar',
          page: 'Pagina',
          more: 'Mas',
          to: 'a',
          of: 'de',
          next: 'Siguiente',
          last: 'Ultima',
          first: 'Primera',
          previous: 'Anterior',
          loadingOoo: 'Cargando...',
        },
        getRowNodeId: data => this.getRowNodeId(data),
        onGridReady: params => {
          this.gridApi = params.api
          this.columnApi = params.columnApi
          this.setGridData()
          this.sizeColumns()
        },
        onGridSizeChanged: () => {
          this.sizeColumns()
        },
      },
    }
  },
  watch: {
    columns: {
      deep: true,
      handler() {
        this.setColumnDefs()
      },
    },
    rows: {
      deep: true,
      handler() {
        this.setGridData()
      },
    },
    quickFilter(value) {
      if (this.gridApi) {
        this.gridApi.setQuickFilter(value)
      }
    },
    localPerPage(value) {
      if (this.gridApi) {
        this.gridApi.paginationSetPageSize(Number(value))
      }
    },
    loading(value) {
      if (!this.gridApi) {
        return
      }

      if (value) {
        this.gridApi.showLoadingOverlay()
      } else if (this.rows.length === 0) {
        this.gridApi.showNoRowsOverlay()
      } else {
        this.gridApi.hideOverlay()
      }
    },
  },
  mounted() {
    this.setColumnDefs()
    this.gridOptions.rowData = this.rows
    this.grid = new Grid(this.$refs.grid, this.gridOptions)
  },
  beforeDestroy() {
    if (this.gridApi) {
      this.gridApi.destroy()
    }
  },
  methods: {
    getRowNodeId(data) {
      const key = data[this.rowKey]
      if (key !== undefined && key !== null) {
        return String(key)
      }

      // fallback autoincremental estable por objeto de fila cuando falta rowKey
      if (!this.autoRowIds.has(data)) {
        this.autoRowIds.set(data, String(++this.autoRowIdSeq))
      }

      return this.autoRowIds.get(data)
    },
    setColumnDefs() {
      const columnDefs = this.columns.map(column => {
        return {
          headerName: column.label,
          field: column.field || column.key,
          width: column.width,
          minWidth: column.minWidth || 140,
          sortable: column.sortable !== false,
          filter: column.filter === false ? false : column.filter || DataTableTextFilter,
          filterParams: column.filterParams,
          floatingFilter: column.floatingFilter === true,
          cellClass: this.getCellClass(column),
          headerClass: this.getHeaderClass(column),
          valueGetter: column.value
            ? params => column.value(params.data)
            : undefined,
          valueFormatter: column.formatter
            ? params => column.formatter(params.value, params.data)
            : undefined,
        }
      })

      this.gridOptions.columnDefs = columnDefs

      if (this.gridApi) {
        this.gridApi.setColumnDefs(columnDefs)
        this.sizeColumns()
      }
    },
    setGridData() {
      if (!this.gridApi) {
        return
      }

      this.gridApi.setRowData(this.rows)

      if (this.loading) {
        this.gridApi.showLoadingOverlay()
      } else if (this.rows.length === 0) {
        this.gridApi.showNoRowsOverlay()
      } else {
        this.gridApi.hideOverlay()
      }
    },
    getCellClass(column) {
      const classes = []

      if (column.align) {
        classes.push(`text-${column.align}`)
      }

      if (column.cellClassName) {
        classes.push(column.cellClassName)
      }

      return classes.join(' ')
    },
    getHeaderClass(column) {
      const classes = []

      if (column.align) {
        classes.push(`text-${column.align}`)
      }

      if (column.className) {
        classes.push(column.className)
      }

      return classes.join(' ')
    },
    clearFilters() {
      this.quickFilter = ''

      if (this.gridApi) {
        this.gridApi.setFilterModel(null)
        this.gridApi.setSortModel(null)
        this.gridApi.onFilterChanged()
      }
    },
    sizeColumns() {
      if (this.gridApi) {
        this.$nextTick(() => this.gridApi.sizeColumnsToFit())
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.data-table {
  width: 100%;
}

.data-table__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.data-table__search {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 320px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #dfe4ee;
  border-radius: 6px;
  color: #6b7280;
  background: #ffffff;
}

.data-table__search-input {
  width: 100%;
  min-width: 0;
  height: 100%;
  margin-left: 8px;
  padding: 0;
  border: 0 !important;
  border-radius: 0 !important;
  outline: 0;
  color: #22292f;
  background: transparent !important;
  box-shadow: none !important;
  font-variant: normal;
  line-height: normal;
  text-align: left;
  text-transform: none;
}

.data-table__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.data-table__page-size {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 0.875rem;
}

.data-table__page-size select {
  height: 34px;
  padding: 0 30px 0 10px;
  border: 1px solid #dfe4ee !important;
  border-radius: 6px !important;
  color: #22292f;
  background-color: #ffffff !important;
  background-image: linear-gradient(45deg, transparent 50%, #21409a 50%), linear-gradient(135deg, #21409a 50%, transparent 50%);
  background-position: calc(100% - 14px) 14px, calc(100% - 9px) 14px;
  background-repeat: no-repeat;
  background-size: 5px 5px, 5px 5px;
  box-shadow: none !important;
  font-variant: normal;
  text-transform: none;
  appearance: none;
}

.data-table__clear {
  height: 34px;
  padding: 0 12px;
  border: 1px solid #dfe4ee;
  border-radius: 6px;
  color: #21409a;
  background: #ffffff;
  cursor: pointer;
}

.data-table__grid {
  width: 100%;
  min-height: 520px;
  border: 1px solid #dfe4ee;
  font-family: inherit;
  font-size: 0.875rem;
}

::v-deep .ag-root-wrapper {
  border: 0;
}

::v-deep .ag-header {
  color: #21409a;
  background: #f8f9fc;
}

::v-deep .ag-header-cell,
::v-deep .ag-cell {
  border-right: 1px solid #dfe4ee;
}

::v-deep .ag-header-cell-label {
  justify-content: center;
}

::v-deep .ag-floating-filter-input {
  height: 28px;
  padding: 0 8px;
  border: 1px solid #dfe4ee !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  font-variant: normal;
  text-align: left;
  text-transform: none;
}

::v-deep .ag-filter-select,
::v-deep .ag-filter-filter,
::v-deep .ag-filter input[type="text"],
::v-deep .ag-filter input[type="number"] {
  width: 100%;
  max-width: 100%;
  height: 32px;
  min-height: 32px;
  padding: 0 8px;
  border: 1px solid #cfd6e4 !important;
  border-radius: 4px !important;
  outline: 0;
  color: #22292f;
  background: #ffffff !important;
  box-shadow: none !important;
  font-family: inherit;
  font-size: 0.875rem;
  font-variant: normal;
  line-height: 32px;
  text-align: left;
  text-transform: none;
}

::v-deep .ag-filter-select {
  padding-right: 30px;
  background-image: linear-gradient(45deg, transparent 50%, #21409a 50%), linear-gradient(135deg, #21409a 50%, transparent 50%);
  background-position: calc(100% - 14px) 13px, calc(100% - 9px) 13px;
  background-repeat: no-repeat;
  background-size: 5px 5px, 5px 5px;
  cursor: pointer;
  appearance: none;
}

::v-deep .ag-filter-select option {
  color: #22292f;
  background: #ffffff;
  font-family: inherit;
  font-size: 0.875rem;
  font-variant: normal;
  text-transform: none;
}

::v-deep .ag-filter input[type="radio"] {
  width: 14px;
  height: 14px;
  margin: 0 6px 0 0;
  border: 1px solid #9aa4b2 !important;
  box-shadow: none !important;
  accent-color: #21409a;
}

::v-deep .ag-filter-condition {
  align-items: center;
  gap: 12px;
  color: #22292f;
  font-size: 0.8125rem;
}

::v-deep .ag-filter-condition label {
  display: inline-flex;
  align-items: center;
  margin: 8px 10px 8px 0;
}

::v-deep .ag-filter-body-wrapper,
::v-deep .ag-filter-wrapper {
  padding: 10px;
  background: #ffffff;
}

::v-deep .ag-filter-apply-panel {
  padding: 8px 10px;
}

::v-deep .text-left {
  text-align: left;
}

::v-deep .text-center {
  text-align: center;
}

::v-deep .text-right {
  text-align: right;
}

@media (max-width: 767px) {
  .data-table__toolbar,
  .data-table__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .data-table__search {
    max-width: none;
  }
}
</style>

<style lang="scss">
.data-table-filter {
  width: 232px;
  padding: 12px;
  color: #22292f;
  background: #ffffff;
  border: 1px solid #dfe4ee;
  border-radius: 6px;
  box-shadow: 0 10px 24px rgba(31, 47, 88, 0.16);
  font-family: 'Montserrat', sans-serif;
}

.data-table-filter__options {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}

.data-table-filter__option {
  width: 100%;
  min-height: 32px;
  padding: 7px 10px;
  border: 1px solid #dfe4ee;
  border-radius: 5px;
  color: #4b5563;
  background: #ffffff;
  font-family: inherit;
  font-size: 0.8125rem;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
}

.data-table-filter__option:hover {
  border-color: #21409a;
  color: #21409a;
  background: #f8f9fc;
}

.data-table-filter__option.is-active {
  border-color: #21409a;
  color: #ffffff;
  background: #21409a;
}

.data-table-filter__field {
  margin-top: 10px;
}

.data-table-filter__input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #cfd6e4 !important;
  border-radius: 5px !important;
  outline: 0;
  color: #22292f;
  background: #ffffff !important;
  box-shadow: none !important;
  font-family: inherit;
  font-size: 0.875rem;
  font-variant: normal;
  line-height: 34px;
  text-align: left;
  text-transform: none;
}

.data-table-filter__input:focus {
  border-color: #21409a !important;
}
</style>
